import { DefaultRootState } from '@databases/redux';
import { ContentRepresentation } from '@databases/types/confluence';
import { ExportFileType, RenderContext } from '@databases/types/database';
import { DatabaseViewSettings } from '@databases/types/database/view';
import { unparse } from 'papaparse';

import { convertContentBody } from '../confluence/content';
import { TranslatableError } from '../errors';
import { parseConfluenceHTML } from '../lib/dom';
import { selectBaseUrl } from '../redux/context/selectors';
import { selectDatabaseByContentIdOptional } from '../redux/databases/selectors';
import { deserializeError, encodeHtml } from '../utils';

import { getStringRepresentation } from './field-types';
import { renderDatabase, renderDatabaseEntry, renderTableLayout } from './render';
import { getDatabaseContentByContentId } from './service';
import {
  getDatabaseEntries,
  getDatabaseEntryRequired,
  getDatabaseFields,
  getDatabaseValue,
  getEntryName,
  isBrokenDatabase,
} from './utils';
import { applyDatabaseView } from './view/view';

export async function exportDatabase(
  {
    contentId,
    exportType,
    viewSettings,
    selectDatabase = selectDatabaseByContentIdOptional,
  }: {
    contentId: string;
    exportType: ExportFileType;
    viewSettings?: DatabaseViewSettings;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
  },
  state: DefaultRootState
): Promise<File> {
  const database = selectDatabase(state, { contentId });
  if (!database || isBrokenDatabase(database)) {
    throw database?.error
      ? deserializeError(database.error)
      : new TranslatableError(
          'Database is not loaded.',
          'snackbar-error-message.database-is-not-loaded'
        );
  }
  const databaseContent = await getDatabaseContentByContentId(contentId);
  const databaseName = databaseContent ? databaseContent.title : database.name;

  switch (exportType) {
    case ExportFileType.HTML:
      return new File(
        [
          await exportDatabaseToHtml(
            {
              contentId,
              renderContext: RenderContext.EXPORT_DATABASE,
              viewSettings,
              selectDatabase,
            },
            state
          ),
        ],
        `${databaseName}.html`,
        { type: 'text/html' }
      );

    case ExportFileType.CSV:
      return new File(
        [exportDatabaseToCSV({ contentId, viewSettings, selectDatabase }, state)],
        `${databaseName}.csv`,
        { type: 'text/csv' }
      );

    default:
      throw new Error(`Export to ${exportType} not supported.`);
  }
}

export function exportDatabaseToCSV(
  {
    contentId,
    viewSettings,
    selectDatabase = selectDatabaseByContentIdOptional,
  }: {
    contentId: string;
    viewSettings?: DatabaseViewSettings;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
  },
  state: DefaultRootState
): string {
  const database = selectDatabase(state, { contentId });
  if (!database || isBrokenDatabase(database)) {
    throw database?.error
      ? deserializeError(database.error)
      : new Error('Database is not loaded.');
  }
  const databaseView = applyDatabaseView(database, database, viewSettings, state);
  const fields = getDatabaseFields(databaseView);
  const fieldNames = fields.map((field) => field.name);
  const entries = getDatabaseEntries(databaseView).map((entry) => {
    return fields.map((field) => {
      const value = getDatabaseValue(database, field, entry);
      const stringRepresentation = getStringRepresentation(
        { database: databaseView, field, entry, value },
        state
      );
      return stringRepresentation || '';
    });
  });

  return unparse<string[]>([fieldNames, ...entries], { skipEmptyLines: true });
}

export async function exportDatabaseToHtml(
  {
    contentId,
    renderContext,
    viewSettings,
    selectDatabase = selectDatabaseByContentIdOptional,
  }: {
    contentId: string;
    renderContext:
      | RenderContext.EXPORT_DATABASE
      | RenderContext.PRINT_PREVIEW
      | RenderContext.COPY_DATABASE_TO_CLIPBOARD;
    viewSettings?: DatabaseViewSettings;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
  },
  state: DefaultRootState
): Promise<string> {
  const database = selectDatabase(state, { contentId });
  if (!database || isBrokenDatabase(database)) {
    throw database?.error
      ? deserializeError(database.error)
      : new Error('Database is not loaded.');
  }

  const databaseContent = await getDatabaseContentByContentId(contentId);
  const databaseName = databaseContent ? databaseContent.title : database.name;

  return await exportToHtml(
    await renderDatabase(
      {
        contentId,
        renderContext,
        viewSettings,
        selectDatabase,
        showName: true,
      },
      state
    ),
    databaseName,
    state,
    renderContext === RenderContext.COPY_DATABASE_TO_CLIPBOARD
  );
}

export async function exportSelectedContentToHtml(
  {
    contentId,
    renderContext,
    selectDatabase = selectDatabaseByContentIdOptional,
    selectedEntryIds,
    selectedFieldIds,
  }: {
    contentId: string;
    renderContext:
      | RenderContext.EXPORT_DATABASE
      | RenderContext.PRINT_PREVIEW
      | RenderContext.COPY_DATABASE_TO_CLIPBOARD;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
    selectedEntryIds: string[];
    selectedFieldIds: string[];
  },
  state: DefaultRootState
): Promise<string> {
  const database = selectDatabase(state, { contentId });
  if (!database || isBrokenDatabase(database)) {
    throw database?.error
      ? deserializeError(database.error)
      : new Error('Database is not loaded.');
  }

  const databaseContent = await getDatabaseContentByContentId(contentId);
  const databaseName = databaseContent ? databaseContent.title : database.name;

  return await exportToHtml(
    await renderTableLayout(
      {
        contentId,
        renderContext,
        selectDatabase,
        selectedEntryIds,
        selectedFieldIds,
      },
      state
    ),
    databaseName,
    state,
    renderContext === RenderContext.COPY_DATABASE_TO_CLIPBOARD
  );
}

export async function exportEntryToHtml(
  {
    contentId,
    entryId,
    renderContext,
    selectDatabase = selectDatabaseByContentIdOptional,
  }: {
    contentId: string;
    entryId: string;
    renderContext:
      | RenderContext.EXPORT_DATABASE
      | RenderContext.PRINT_PREVIEW
      | RenderContext.COPY_DATABASE_TO_CLIPBOARD;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
  },
  state: DefaultRootState
): Promise<string> {
  const database = selectDatabase(state, { contentId });
  if (!database || isBrokenDatabase(database)) {
    throw database?.error
      ? deserializeError(database.error)
      : new Error('Database is not loaded.');
  }
  const entry = getDatabaseEntryRequired(database, entryId);
  return await exportToHtml(
    await renderDatabaseEntry(
      { contentId, entryId, renderContext, selectDatabase, showName: true },
      state
    ),
    getEntryName({ database, entry }, state) || '',
    state,
    renderContext === RenderContext.COPY_DATABASE_TO_CLIPBOARD
  );
}

async function exportToHtml(
  renderedStorageHtml: string,
  title: string,
  state: DefaultRootState,
  contentOnly = false
): Promise<string> {
  const body = await convertContentBody(
    { representation: ContentRepresentation.STORAGE, value: renderedStorageHtml },
    { to: ContentRepresentation.EXPORT_VIEW },
    ['webresource.uris.css', 'webresource.superbatch.uris.css']
  );

  // The result is not valid HTML, as it uses self-closing tags such as <div/> (but it is also not valid XHTML).
  // We need to convert it to valid HTML so that the browsers will interpret the result correctly.
  const html = parseConfluenceHTML(body.value).html()!;

  if (contentOnly) {
    return html;
  } else {
    return (
      `<!DOCTYPE html>` +
      `<html><head><title>${encodeHtml(title)}</title>` +
      `<base href=${encodeHtml(selectBaseUrl(state))}>` +
      `<style>body { background: #fff !important; padding: 15px !important; }</style>` +
      [
        ...(body.webresource?.uris?.css ?? []),
        ...(body.webresource?.superbatch?.uris?.css ?? []),
      ]
        .map((uri) => `<link rel="stylesheet" href="${encodeHtml(uri)}">`)
        .join('') +
      `</head><body>` +
      `<div class="wiki-content">${html}</div>` +
      `</body></html>`
    );
  }
}
